/**
 * Las ofertas (2026-11-03, the owner: «a tool that suggests to me products that haven't been moving so
 * well … which products to put on sale and for how long, basically a promotion generator»): a
 * product's prices on one list, lower, for some days (`oferta.creada`), and ended by hand before
 * their last (`oferta.terminada`). Or, for a presentation, so many for the price of fewer («2×1»,
 * «lleva 3, paga 2»): its line at the list's price, and a line of the free ones at nothing.
 *
 * They book nothing. What an offer sells books as any sale does, at what it was charged, and its cost
 * is the cost; what the books keep is the offer itself, for every till to charge it while it lasts
 * (`ofertaDe` on the phone), and the rule that a product has one at a time on a list: two would leave
 * the till to choose between them.
 */

import { diasEntre, orderKey, sumarDias, type AnyDomainEvent, type Centavos } from '@abastece/shared';
import type { ApplyResult, Oferta, Reader } from '../state.js';

/** The longest an offer runs: past three months it is the price, and «Precios» is where that is set. */
export const DIAS_MAXIMOS_OFERTA = 92;

/**
 * The last day an offer is charged: its own, or the day before the one it was ended on. The day it is
 * ended it stops as soon as the till knows, so another may start that very day.
 */
export function ultimoDiaDeOferta(o: Pick<Oferta, 'hasta' | 'terminadaEl'>): string {
  return o.terminadaEl !== null && o.terminadaEl <= o.hasta ? sumarDias(o.terminadaEl, -1) : o.hasta;
}

export async function reduceOferta(event: AnyDomainEvent, read: Reader): Promise<ApplyResult | null> {
  const rev = orderKey(event);
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  switch (event.type) {
    case 'oferta.creada': {
      const p = event.payload;
      if (await read.oferta(p.ofertaId)) return rechazo('Esa oferta ya existe');
      const producto = await read.producto(p.productoId);
      if (!producto) return rechazo('El producto no existe');
      if (!producto.activo) return rechazo(`${producto.nombre} ya no se vende`);
      const lista = await read.listaPrecio(p.listaId);
      if (!lista || !lista.activa) return rechazo('La lista de precios no existe');

      const suyas = new Set((await read.unidadesDeProducto(p.productoId)).map((u) => u.id));
      const vistas = new Set<string>();
      for (const x of p.precios) {
        if (!suyas.has(x.unidadVentaId)) return rechazo(`Esa presentación no es de ${producto.nombre}`);
        if (vistas.has(x.unidadVentaId)) return rechazo('Cada presentación va una sola vez en la oferta');
        vistas.add(x.unidadVentaId);
      }
      if (diasEntre(p.desde, p.hasta) + 1 > DIAS_MAXIMOS_OFERTA) return rechazo(`Una oferta dura ${DIAS_MAXIMOS_OFERTA} días a lo más`);

      // One at a time on a list: the days of one that is ended count up to the day before it was.
      const otra = (await read.ofertasDeProducto(p.productoId)).find(
        (o) => o.listaId === p.listaId && o.desde <= p.hasta && p.desde <= ultimoDiaDeOferta(o),
      );
      if (otra) return rechazo(`${producto.nombre} ya está en oferta del ${otra.desde} al ${ultimoDiaDeOferta(otra)}`);

      const row: Oferta = {
        id: p.ofertaId,
        productoId: p.productoId,
        listaId: p.listaId,
        // A lower price, or so many for fewer: the schema has made sure it is one of the two.
        precios: p.precios.map((x) =>
          x.lleva !== undefined ? { unidadVentaId: x.unidadVentaId, lleva: x.lleva, paga: x.paga! } : { unidadVentaId: x.unidadVentaId, precio: x.precio! as Centavos },
        ),
        desde: p.desde,
        hasta: p.hasta,
        motivo: p.motivo,
        nota: p.nota ?? null,
        terminadaEl: null,
        rev,
      };
      return { effects: [{ kind: 'oferta.upsert', id: p.ofertaId, row }], rejections: [] };
    }

    case 'oferta.terminada': {
      const p = event.payload;
      const o = await read.oferta(p.ofertaId);
      if (!o) return rechazo('Esa oferta no existe');
      if (o.terminadaEl !== null) return rechazo('Esa oferta ya se terminó');
      if (p.fecha > o.hasta) return rechazo(`Esa oferta ya terminó el ${o.hasta}`);
      return { effects: [{ kind: 'oferta.patch', id: o.id, rev, patch: { terminadaEl: p.fecha } }], rejections: [] };
    }

    default:
      return null;
  }
}
